import React, { useState, useEffect, useCallback } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { API_URL as API } from '@/utils/apiConfig';
import {
  BarChart3, TrendingUp, TrendingDown, Users, DollarSign, MessageSquare, Shield,
  Calendar, Clock, RefreshCw, Loader2, AlertTriangle, ChevronDown, ChevronRight,
  Zap, UserMinus, Search, Settings2, Minus, Globe, Mail, UserPlus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';


export default function AdminDigestsPage() {
  const { colors } = useAdminTheme();
  const [digests, setDigests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showSchedule, setShowSchedule] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchDigests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (filter !== 'all') params.append('period', filter);
      const res = await axios.get(`${API}/admin/digests?${params}`, { withCredentials: true });
      setDigests(res.data.digests || []);
      setTotalPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load digests');
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { fetchDigests(); }, [fetchDigests]);

  const handleGenerate = async (period) => {
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/digests/generate?period=${period}`, {}, { withCredentials: true });
      toast.success(`${period.charAt(0).toUpperCase() + period.slice(1)} digest generated`);
      fetchDigests();
    } catch {
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-digests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5" style={{ color: colors.accent }} />
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Digests</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="ghost" className="text-xs gap-1.5" style={{ color: colors.textSecondary }}
              onClick={() => setShowSchedule(!showSchedule)}>
              <Settings2 className="h-3.5 w-3.5" /> Schedule
            </Button>
            <Button size="sm" variant="ghost" className="text-xs gap-1.5" style={{ color: colors.textSecondary }}
              onClick={fetchDigests} disabled={loading}>
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            <Button size="sm" className="text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white"
              onClick={() => handleGenerate('daily')} disabled={generating} data-testid="generate-daily-btn">
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
              Generate Daily
            </Button>
            <Button size="sm" className="text-xs gap-1.5 bg-violet-600 hover:bg-violet-500 text-white"
              onClick={() => handleGenerate('weekly')} disabled={generating} data-testid="generate-weekly-btn">
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
              Generate Weekly
            </Button>
          </div>
        </div>

        {/* Schedule Config */}
        {showSchedule && <ScheduleConfig colors={colors} />}

        {/* Filter tabs */}
        <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
          {['all', 'daily', 'weekly'].map(f => (
            <button key={f} onClick={() => { setFilter(f); setPage(1); }}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? 'text-white' : ''}`}
              style={filter === f ? { background: colors.accent } : { color: colors.textMuted }}
              data-testid={`filter-${f}`}
            >{f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>

        {/* Digest List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.textMuted }} /></div>
        ) : digests.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
            <BarChart3 className="h-8 w-8 mx-auto mb-3" style={{ color: colors.textMuted }} />
            <p className="text-sm" style={{ color: colors.textMuted }}>No digests yet. Generate your first one above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {digests.map(d => (
              <DigestCard key={d.digest_id} digest={d} colors={colors}
                expanded={expandedId === d.digest_id}
                onToggle={() => setExpandedId(expandedId === d.digest_id ? null : d.digest_id)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              style={{ color: colors.textMuted }}>Previous</Button>
            <span className="text-xs" style={{ color: colors.textMuted }}>Page {page} of {totalPages}</span>
            <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              style={{ color: colors.textMuted }}>Next</Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}


function DigestCard({ digest, colors, expanded, onToggle }) {
  const m = digest.metrics;
  const u = m.users;
  const r = m.revenue;
  const pr = m.prev_revenue;
  const msg = m.messages;
  const sec = m.security;
  const churn = m.churn;
  const alerts = digest.alerts || [];
  const hasAlerts = alerts.length > 0;
  const highAlerts = alerts.filter(a => a.severity === 'high').length;
  const date = new Date(digest.generated_at);
  const isWeekly = digest.period === 'weekly';

  return (
    <div className="rounded-2xl overflow-hidden transition-all" data-testid={`digest-${digest.digest_id}`}
      style={{ background: colors.card, border: `1px solid ${hasAlerts && highAlerts > 0 ? 'rgba(239,68,68,0.4)' : colors.cardBorder}` }}>
      {/* Header bar */}
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:opacity-80 transition-opacity">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isWeekly ? 'bg-violet-500' : 'bg-indigo-500'}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold" style={{ color: colors.text }}>
                {isWeekly ? 'Weekly' : 'Daily'} Digest
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: isWeekly ? 'rgba(139,92,246,0.15)' : 'rgba(99,102,241,0.15)', color: isWeekly ? '#a78bfa' : '#818cf8' }}>
                {isWeekly ? '7 days' : '24h'}
              </span>
              {hasAlerts && (
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${highAlerts > 0 ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                  {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <span className="text-[11px]" style={{ color: colors.textMuted }}>
              {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {/* Quick stats in header */}
          <div className="hidden sm:flex items-center gap-6">
            <QuickStat icon={Users} label="New" value={u.new_signups} prev={u.prev_signups} colors={colors} />
            <QuickStat icon={DollarSign} label="Revenue" value={`$${r.total.toFixed(0)}`} prev={pr.total} isCurrency colors={colors} />
            <QuickStat icon={MessageSquare} label="Messages" value={msg.total} prev={msg.prev_total} colors={colors} />
          </div>
          {expanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" style={{ color: colors.textMuted }} />
            : <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: colors.textMuted }} />}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-5 space-y-5 border-t" style={{ borderColor: colors.cardBorder }}>
          {/* Alerts */}
          {hasAlerts && (
            <div className="mt-4 space-y-2" data-testid="digest-alerts">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-red-400">Critical Alerts</h3>
              {alerts.map((a, i) => (
                <div key={a.category + a.message.slice(0, 20) + i} className="flex items-start gap-2.5 rounded-xl px-3 py-2.5"
                  style={{ background: a.severity === 'high' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)', border: `1px solid ${a.severity === 'high' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}` }}>
                  <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${a.severity === 'high' ? 'text-red-400' : 'text-amber-400'}`} />
                  <div>
                    <span className={`text-[10px] uppercase font-bold ${a.severity === 'high' ? 'text-red-400' : 'text-amber-400'}`}>{a.category}</span>
                    <p className="text-xs" style={{ color: colors.text }}>{a.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Metrics grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <MetricCard icon={Users} title="Total Users" value={u.total} colors={colors} />
            <MetricCard icon={Users} title="New Signups" value={u.new_signups} prev={u.prev_signups} colors={colors} accent="indigo" />
            <MetricCard icon={Users} title="Active Users" value={u.active} prev={u.prev_active} colors={colors} accent="emerald" />
            <MetricCard icon={Users} title="Guests" value={u.new_guests} colors={colors} accent="sky" />
            <MetricCard icon={DollarSign} title="Revenue" value={`$${r.total.toFixed(2)}`} prev={pr.total} isCurrency colors={colors} accent="green" />
            <MetricCard icon={DollarSign} title="Transactions" value={r.count} colors={colors} accent="green" />
            <MetricCard icon={MessageSquare} title="Messages" value={msg.total} prev={msg.prev_total} colors={colors} accent="blue" />
            <MetricCard icon={MessageSquare} title="New Conversations" value={msg.new_conversations} colors={colors} accent="blue" />
          </div>

          {/* Second row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard icon={Shield} title="Bot Events" value={sec.bot_events} colors={colors} accent="amber" invertTrend />
            <MetricCard icon={Shield} title="Content Filtered" value={sec.content_filter_blocks} colors={colors} accent="amber" invertTrend />
            <MetricCard icon={Shield} title="Locked Accounts" value={sec.locked_accounts} colors={colors} accent="red" invertTrend />
            <MetricCard icon={UserMinus} title="Cancellations" value={churn.cancelled_subscriptions} colors={colors} accent="red" invertTrend />
          </div>

          {/* Subscriptions breakdown */}
          <div className="rounded-xl p-4" style={{ background: `${colors.card}cc`, border: `1px solid ${colors.cardBorder}` }}>
            <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textMuted }}>Subscription Mix</h3>
            <div className="flex items-center gap-4 flex-wrap">
              {[
                { label: 'Free', value: m.subscriptions.free, color: '#64748b' },
                { label: 'Paid', value: m.subscriptions.paid, color: '#6366f1' },
                { label: 'Premium', value: m.subscriptions.premium, color: '#8b5cf6' },
                { label: 'Share', value: m.subscriptions.share, color: '#10b981' },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-xs" style={{ color: colors.textSecondary }}>{s.label}</span>
                  <span className="text-sm font-bold" style={{ color: colors.text }}>{s.value}</span>
                </div>
              ))}
            </div>
            {u.total > 0 && (
              <div className="flex h-2 rounded-full overflow-hidden mt-3 gap-0.5">
                {[
                  { v: m.subscriptions.free, c: '#64748b' },
                  { v: m.subscriptions.paid, c: '#6366f1' },
                  { v: m.subscriptions.premium, c: '#8b5cf6' },
                  { v: m.subscriptions.share, c: '#10b981' },
                ].map((s) => (
                  <div key={s.c} style={{ width: `${(s.v / u.total) * 100}%`, background: s.c, minWidth: s.v > 0 ? '4px' : 0 }} className="rounded-full" />
                ))}
              </div>
            )}
          </div>

          {/* Top Queries */}
          {digest.top_queries && digest.top_queries.length > 0 && (
            <div className="rounded-xl p-4" style={{ background: `${colors.card}cc`, border: `1px solid ${colors.cardBorder}` }}>
              <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textMuted }}>Top Queries</h3>
              <div className="space-y-1.5">
                {digest.top_queries.map((q, i) => (
                  <div key={q.query} className="flex items-center gap-3">
                    <span className="text-[10px] font-bold w-5 text-right" style={{ color: colors.textMuted }}>{i + 1}</span>
                    <span className="text-xs truncate flex-1" style={{ color: colors.textSecondary }}>{q.query}</span>
                    <span className="text-xs font-medium" style={{ color: colors.text }}>{q.count}x</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Users Detail */}
          {digest.new_users && digest.new_users.length > 0 && (
            <NewUsersDetail users={digest.new_users} colors={colors} />
          )}
        </div>
      )}
    </div>
  );
}


function QuickStat({ icon: Icon, label, value, prev, isCurrency, colors }) {
  const trend = prev !== undefined ? (typeof value === 'string' ? parseFloat(value.replace('$', '')) : value) - (typeof prev === 'string' ? parseFloat(String(prev).replace('$', '')) : prev) : 0;
  return (
    <div className="text-right">
      <p className="text-[11px]" style={{ color: colors.textMuted }}>{label}</p>
      <div className="flex items-center justify-end gap-1">
        <span className="text-sm font-bold" style={{ color: colors.text }}>{value}</span>
        {trend !== 0 && (
          trend > 0 ? <TrendingUp className="h-3 w-3 text-emerald-400" /> : <TrendingDown className="h-3 w-3 text-red-400" />
        )}
      </div>
    </div>
  );
}


function MetricCard({ icon: Icon, title, value, prev, isCurrency, colors, accent = 'slate', invertTrend }) {
  const numVal = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value;
  const numPrev = prev !== undefined ? (typeof prev === 'string' ? parseFloat(String(prev).replace(/[$,]/g, '')) : prev) : null;
  const diff = numPrev !== null ? numVal - numPrev : null;
  const pctChange = numPrev && numPrev > 0 ? ((diff / numPrev) * 100).toFixed(0) : null;

  let trendColor = 'text-gray-400';
  if (diff !== null && diff !== 0) {
    const isPositive = invertTrend ? diff < 0 : diff > 0;
    trendColor = isPositive ? 'text-emerald-400' : 'text-red-400';
  }

  const accentColors = {
    indigo: 'rgba(99,102,241,0.12)', emerald: 'rgba(16,185,129,0.12)', blue: 'rgba(59,130,246,0.12)',
    green: 'rgba(34,197,94,0.12)', amber: 'rgba(245,158,11,0.12)', red: 'rgba(239,68,68,0.12)',
    sky: 'rgba(14,165,233,0.12)', slate: 'rgba(100,116,139,0.12)',
  };

  return (
    <div className="rounded-xl p-3.5" style={{ background: accentColors[accent] || accentColors.slate, border: `1px solid ${colors.cardBorder}` }}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3 w-3" style={{ color: colors.textMuted }} />
        <span className="text-[11px]" style={{ color: colors.textMuted }}>{title}</span>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-lg font-bold" style={{ color: colors.text }}>{value}</span>
        {pctChange !== null && (
          <span className={`text-[11px] font-medium flex items-center gap-0.5 ${trendColor}`}>
            {diff > 0 ? <TrendingUp className="h-3 w-3" /> : diff < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            {Math.abs(pctChange)}%
          </span>
        )}
      </div>
    </div>
  );
}


function countryFlag(code) {
  if (!code) return '';
  return code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt()));
}

function NewUsersDetail({ users, colors }) {
  const { isDark } = useAdminTheme();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? users : users.slice(0, 5);

  return (
    <div className="rounded-xl p-4" style={{ background: `${colors.card}cc`, border: `1px solid ${colors.cardBorder}` }} data-testid="digest-new-users">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserPlus className="h-3.5 w-3.5 text-emerald-400" />
          <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>New Users ({users.length})</h3>
        </div>
        {users.length > 5 && (
          <button onClick={() => setExpanded(!expanded)} className="text-[11px] font-medium hover:underline" style={{ color: colors.accent }}>
            {expanded ? 'Show less' : `Show all ${users.length}`}
          </button>
        )}
      </div>
      <div className="space-y-1.5">
        {shown.map((u, i) => {
          const tier = u.subscription_tier || 'free';
          const tierColors = { free: '#64748b', paid: '#6366f1', premium: '#8b5cf6', share: '#10b981' };
          const date = new Date(u.created_at);
          return (
            <div key={u.user_id || u.email || i} className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all"
              onMouseEnter={(e) => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              data-testid={`digest-user-${i}`}>
              <span className="text-base leading-none w-6 text-center shrink-0">{countryFlag(u.country_code)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium truncate" style={{ color: colors.text }}>{u.name || 'Unnamed'}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-md font-medium shrink-0" style={{ background: `${tierColors[tier]}20`, color: tierColors[tier] }}>
                    {tier}
                  </span>
                  {u.auth_method && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-md shrink-0" style={{ background: u.auth_method === 'google' ? 'rgba(234,179,8,0.12)' : 'rgba(99,102,241,0.12)', color: u.auth_method === 'google' ? '#eab308' : '#818cf8' }}>
                      {u.auth_method === 'google' ? 'Google' : 'Email'}
                    </span>
                  )}
                </div>
                <span className="text-[11px] truncate block" style={{ color: colors.textMuted }}>{u.email}</span>
              </div>
              <div className="text-right shrink-0">
                <span className="text-[11px]" style={{ color: colors.textMuted }}>
                  {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <span className="text-[10px] block" style={{ color: colors.textMuted }}>
                  {u.country || ''}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ScheduleConfig({ colors }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/digests/schedule/config`, { withCredentials: true })
      .then(res => setSchedule(res.data))
      .catch(() => toast.error('Failed to load schedule'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/digests/schedule/config`, schedule, { withCredentials: true });
      toast.success('Schedule saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !schedule) return null;

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <div className="rounded-2xl p-5 space-y-4" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid="digest-schedule">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4" style={{ color: colors.accent }} />
        <h2 className="text-sm font-semibold" style={{ color: colors.text }}>Notification Schedule</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Daily */}
        <div className="space-y-2.5">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={schedule.daily_enabled}
              onChange={e => setSchedule({ ...schedule, daily_enabled: e.target.checked })}
              className="rounded" data-testid="daily-enabled" />
            <span className="text-xs font-medium" style={{ color: colors.text }}>Daily Digest</span>
          </label>
          {schedule.daily_enabled && (
            <div>
              <label className="text-[11px]" style={{ color: colors.textMuted }}>Send notification at</label>
              <input type="time" value={schedule.daily_time} style={inputStyle}
                onChange={e => setSchedule({ ...schedule, daily_time: e.target.value })}
                className="w-full rounded-lg px-3 py-1.5 text-xs mt-1" data-testid="daily-time" />
            </div>
          )}
        </div>

        {/* Weekly */}
        <div className="space-y-2.5">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={schedule.weekly_enabled}
              onChange={e => setSchedule({ ...schedule, weekly_enabled: e.target.checked })}
              className="rounded" data-testid="weekly-enabled" />
            <span className="text-xs font-medium" style={{ color: colors.text }}>Weekly Digest</span>
          </label>
          {schedule.weekly_enabled && (
            <div className="space-y-2">
              <div>
                <label className="text-[11px]" style={{ color: colors.textMuted }}>Day</label>
                <select value={schedule.weekly_day} style={inputStyle}
                  onChange={e => setSchedule({ ...schedule, weekly_day: e.target.value })}
                  className="w-full rounded-lg px-3 py-1.5 text-xs mt-1" data-testid="weekly-day">
                  {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                    <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px]" style={{ color: colors.textMuted }}>Time</label>
                <input type="time" value={schedule.weekly_time} style={inputStyle}
                  onChange={e => setSchedule({ ...schedule, weekly_time: e.target.value })}
                  className="w-full rounded-lg px-3 py-1.5 text-xs mt-1" data-testid="weekly-time" />
              </div>
            </div>
          )}
        </div>
      </div>

      <Button onClick={save} disabled={saving} size="sm" className="text-xs gap-1.5"
        style={{ background: colors.accent }} data-testid="save-schedule-btn">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calendar className="h-3.5 w-3.5" />}
        Save Schedule
      </Button>
    </div>
  );
}
